import {
    Add as AddIcon,
    ContentPaste as ContentPasteIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Timer as TimerIcon,
    Warning as WarningIcon,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    DialogContent,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    LinearProgress,
    Paper,
    Slider,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LovenseSettingItem, LovenseToyItem, ToyAction } from '../../../../hooks/useLovense';
import useLovenseSettings from '../../../../hooks/useLovenseSettings';
import useSubscriber from '../../../../hooks/useSubscriber';
import { SettingsContext } from '../../../../store/SettingsContext';
import DialogAdaptive from '../../../common/DialogAdaptive';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';
import StyledDialogActions from '../../../common/StyledDialogActions';

// --- Types ---

export interface TipRangeRule {
    id: string;
    minTip: number;
    maxTip: number | null; // null represents "and above" (Infinity)
    secondsPerTip: number; // Rate of action duration
    actions: ToyAction[];
}

// --- Styles for Clean Number Inputs ---
const cleanNumberStyle = {
    '& input[type=number]': {
        '-moz-appearance': 'textfield',
    },
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0,
    },
};

// --- Helper Functions ---

/**
 * Checks if two ranges [minA, maxA] and [minB, maxB] overlap.
 * Null max represents Infinity.
 */
// --- Helper Functions ---

const doRangesOverlap = (startA: number, endA: number, startB: number, endB: number): boolean => {
    return startA < endB && startB < endA;
};

// Convert API intensity (0-20) to Percentage (0-100)
const toPercent = (val: number | undefined) => Math.round(((val ?? 0) / 20) * 100);

// Convert Percentage (0-100) to API intensity (0-20)
const toIntensity = (percent: number) => Math.round((percent / 100) * 20);

// --- Main Component ---

const ToySettingsEditor = ({ toy }: { toy: LovenseToyItem }) => {
    const settingsContext = useContext(SettingsContext);

    const { enqueueSnackbar } = useSnackbar();

    const { data: subscriber } = useSubscriber();
    const { data: allSettings, saveLovenseSettings, isUpdatingSettings, deleteLovenseSettings } = useLovenseSettings();

    const [isDialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<Partial<LovenseSettingItem> | null>(null);

    // Filter settings for this specific toy
    const toySettings = useMemo(() => {
        if (!allSettings) return [];
        return allSettings.filter(item => item.toyId === toy.toyId).sort((a, b) => a.startDollarAmount - b.startDollarAmount);
    }, [allSettings, toy.toyId]);

    // -- Validation Logic --
    const validationError = useMemo(() => {
        if (!editingItem) return null;

        const start = editingItem.startDollarAmount ?? 0;
        const end = editingItem.endingDollarAmount ?? 0;
        const duration = editingItem.durationPerDollar ?? 0;

        // 1. Basic Bounds
        if (start < 1) return 'Start amount must be at least $1.';
        if (end <= start) return 'End amount must be greater than start amount.';
        if (end > 200) return 'Maximum tip amount cannot exceed $200.';
        if (duration <= 0) return 'Duration must be greater than 0.';

        // 2. Overlap Checks
        // Filter out the item currently being edited (if it exists) so we don't compare against itself
        const otherSettings = toySettings.filter(s => s.settingId !== editingItem.settingId);

        for (const setting of otherSettings) {
            if (doRangesOverlap(start, end, setting.startDollarAmount, setting.endingDollarAmount)) {
                return `Ranges overlap with existing setting ($${setting.startDollarAmount}-$${setting.endingDollarAmount}).`;
            }
        }

        return null;
    }, [editingItem, toySettings]);

    // -- Handlers --

    const handleOpenDialog = (item?: LovenseSettingItem) => {
        if (item) {
            setEditingItem({ ...item });
        } else {
            const maxExistingEnd = toySettings.reduce((max, curr) => Math.max(max, curr.endingDollarAmount), 0);
            const suggestedStart = maxExistingEnd > 0 ? Math.min(maxExistingEnd + 1, 200) : 1;
            const suggestedEnd = Math.min(suggestedStart + 9, 200);

            setEditingItem({
                toyId: toy.toyId,
                // @ts-ignore
                onlyfansId: toy.onlyfansId || 0,
                startDollarAmount: suggestedStart,
                endingDollarAmount: suggestedEnd,
                durationPerDollar: 1,
                intensity: 10, // Default to 50%
                durationMode: 'dynamic',
                pattern: 'nothing',
            });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingItem(null);
    };

    const handleRangeChange = (event: Event, newValue: number | number[], activeThumb: number) => {
        if (!Array.isArray(newValue) || !editingItem) return;

        const minDistance = 1;
        const [newStart, newEnd] = newValue;

        if (activeThumb === 0) {
            // Moving start handle: clamp it so it doesn't pass (end - 1)
            setEditingItem({
                ...editingItem,
                startDollarAmount: Math.min(newStart, (editingItem.endingDollarAmount || 200) - minDistance),
            });
        } else {
            // Moving end handle: clamp it so it doesn't pass (start + 1)
            setEditingItem({
                ...editingItem,
                endingDollarAmount: Math.max(newEnd, (editingItem.startDollarAmount || 1) + minDistance),
            });
        }
    };

    const handleSave = async () => {
        if (!editingItem || validationError) return;

        const payload: LovenseSettingItem = {
            toyId: toy.toyId,
            onlyfansId: toy.onlyfansId || 0,
            settingId: editingItem.settingId,
            startDollarAmount: Number(editingItem.startDollarAmount),
            endingDollarAmount: Number(editingItem.endingDollarAmount),
            durationPerDollar: Number(editingItem.durationPerDollar),
            intensity: Number(editingItem.intensity),
            durationMode: editingItem.durationMode,
            pattern: editingItem.pattern,
        };

        await saveLovenseSettings(payload)
            .then(() => {
                enqueueSnackbar(`Lovense setting ${payload.settingId ? 'updated' : 'created'} successfully.`, {
                    variant: 'success',
                    autoHideDuration: 3000,
                });
            })
            .catch(error => {
                enqueueSnackbar(`Failed to save Lovense setting: ${error.message}`, {
                    variant: 'error',
                    autoHideDuration: 3000,
                });
            });

        handleCloseDialog();
    };

    const handleDelete = async (settingId: string | undefined) => {
        if (settingId && deleteLovenseSettings) {
            await deleteLovenseSettings(settingId)
                .then(result => {
                    if (result && result.success) {
                        enqueueSnackbar('Lovense setting deleted successfully.', {
                            variant: 'success',
                            autoHideDuration: 3000,
                        });
                    } else {
                        enqueueSnackbar('Lovense setting not found or could not be deleted.', {
                            variant: 'warning',
                            autoHideDuration: 3000,
                        });
                    }
                })
                .catch(error => {
                    enqueueSnackbar(`Failed to delete Lovense setting: ${error.message}`, {
                        variant: 'error',
                        autoHideDuration: 3000,
                    });
                });
        }
    };

    return (
        <Box>
            {/* Header */}
            <Button
                variant="contained"
                fullWidth
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                disabled={isUpdatingSettings}
                sx={{ mb: 2 }}
            >
                Add Range
            </Button>

            {/* Loading Indicator */}
            {isUpdatingSettings && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

            {/* Settings List */}
            <Stack spacing={2}>
                {(!toySettings || toySettings.length === 0) && (
                    <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default' }} variant="outlined">
                        <Typography color="text.secondary">No tip settings configured for this device.</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Tip ranges must not overlap. Maximum tip amount is $200.
                        </Typography>
                    </Paper>
                )}

                {toySettings.map(setting => {
                    const percent = toPercent(setting.intensity);
                    return (
                        <Card key={setting.settingId || Math.random()} variant="outlined">
                            <CardContent>
                                <Grid container alignItems="center" spacing={2}>
                                    {/* Range Badge */}
                                    <Grid item xs="auto">
                                        <Box
                                            sx={{
                                                bgcolor: 'success.main',
                                                color: 'success.contrastText',
                                                borderRadius: 2,
                                                minWidth: 110,
                                                height: 56,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                px: 1,
                                                boxShadow: 2,
                                            }}
                                        >
                                            <Typography fontWeight="bold" variant="subtitle1" sx={{ lineHeight: 1.2 }}>
                                                ${setting.startDollarAmount} - ${setting.endingDollarAmount}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{ opacity: 0.9, fontSize: '0.65rem', textTransform: 'uppercase' }}
                                            >
                                                Range
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* Details */}
                                    <Grid item xs>
                                        <Stack spacing={1}>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <TimerIcon fontSize="small" color="action" />
                                                <Typography variant="body2">
                                                    <b>{setting.durationPerDollar}s</b>
                                                    {setting.durationMode === 'dynamic' ? ' per dollar' : ''}
                                                </Typography>
                                            </Box>
                                            <Divider flexItem />
                                            {/* Intensity Visualizer */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Intensity:
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        height: 6,
                                                        bgcolor: 'action.hover',
                                                        borderRadius: 1,
                                                        overflow: 'hidden',
                                                        maxWidth: 100,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: `${percent}%`,
                                                            height: '100%',
                                                            bgcolor: 'primary.main',
                                                        }}
                                                    />
                                                </Box>
                                                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 24 }}>
                                                    {percent}%
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Grid>

                                    {/* Actions */}
                                    <Grid item xs="auto" display="flex" flexDirection="column" gap={1} alignItems="flex-end">
                                        <IconButton
                                            onClick={() => handleOpenDialog(setting)}
                                            color="primary"
                                            size="small"
                                            disabled={isUpdatingSettings}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleDelete(setting.settingId)}
                                            color="error"
                                            size="small"
                                            disabled={isUpdatingSettings}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    );
                })}

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Use the button below to copy your current tip menu settings to the clipboard for easy sharing during live streams.
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Or better yet, paste them into the{' '}
                    <Link
                        to={`/subscribers/${subscriber._id}/${settingsContext.services.sextforce.homeUrl}/liveStream/autoAnnouncements`}
                        style={{ textDecoration: 'none', fontWeight: 'bold' }}
                    >
                        Auto Announcement
                    </Link>{' '}
                    system to automatically inform your viewers of the tip menu!
                </Typography>

                <Button
                    variant="outlined"
                    disabled={!toySettings || toySettings.length === 0}
                    fullWidth
                    startIcon={<ContentPasteIcon />}
                    onClick={() => {
                        const settingsText =
                            `Tip Menu\n--------\n` +
                            toySettings
                                .map(
                                    s =>
                                        `Tip $${s.startDollarAmount}-$${s.endingDollarAmount}: 💪 ${toPercent(s.intensity)}% ⏱️ ${
                                            s.durationPerDollar
                                        } sec${s.durationMode === 'dynamic' ? ' per $' : ''}`,
                                )
                                .join('\n');

                        navigator.clipboard.writeText(settingsText);

                        enqueueSnackbar('Tip menu copied to clipboard', { variant: 'info', autoHideDuration: 3000 });
                    }}
                >
                    Copy Tips Menu to Clipboard
                </Button>
            </Stack>

            {/* Edit Dialog */}
            <DialogAdaptive open={isDialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                <StyledCardTitleBar title={editingItem?.settingId ? 'Edit Setting' : 'New Setting'} />

                {editingItem && (
                    <DialogContent sx={{ p: 1.5 }}>
                        {/* Validation Alert */}
                        {validationError && (
                            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 1 }}>
                                {validationError}
                            </Alert>
                        )}

                        {/* Range Slider Section */}
                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Tip Range ($)
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Define the tip amount range that will trigger this setting.
                        </Typography>

                        <Box
                            sx={{
                                bgcolor: 'action.hover',
                                px: 3,
                                py: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                mb: 2,
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Start: <b>${editingItem.startDollarAmount}</b>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    End: <b>${editingItem.endingDollarAmount}</b>
                                </Typography>
                            </Box>

                            <Slider
                                value={[editingItem.startDollarAmount || 1, editingItem.endingDollarAmount || 200]}
                                onChange={handleRangeChange}
                                valueLabelDisplay="auto"
                                disableSwap
                                min={1}
                                max={200}
                                marks={[
                                    { value: 1, label: '$1' },
                                    { value: 100, label: '$100' },
                                    { value: 200, label: '$200' },
                                ]}
                            />
                        </Box>

                        {/* Timing */}
                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Timing
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Set how long the toy will run. In dynamic mode, duration is calculated per dollar tipped within this range. In
                            fixed mode, the duration is constant regardless of tip amount.
                        </Typography>
                        <Grid container spacing={2} mb={2}>
                            <Grid item xs={12}>
                                <ToggleButtonGroup
                                    value={editingItem.durationMode || 'dynamic'}
                                    color="primary"
                                    size="medium"
                                    exclusive
                                    onChange={(_, value) => {
                                        if (value) {
                                            setEditingItem({ ...editingItem, durationMode: value });
                                        }
                                    }}
                                    fullWidth
                                >
                                    <ToggleButton
                                        value="dynamic"
                                        // variant={editingItem.durationMode === 'dynamic' ? 'contained' : 'outlined'}
                                    >
                                        Dynamic
                                    </ToggleButton>
                                    <ToggleButton
                                        value="fixed"
                                        // variant={editingItem.durationMode === 'fixed' ? 'contained' : 'outlined'}
                                    >
                                        Fixed
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={editingItem.durationMode === 'dynamic' ? 'Duration per $' : 'Fixed Duration (seconds)'}
                                    type="number"
                                    fullWidth
                                    sx={cleanNumberStyle}
                                    value={editingItem.durationPerDollar}
                                    onChange={e => {
                                        const val = Number(e.target.value);
                                        if (val >= 0) setEditingItem({ ...editingItem, durationPerDollar: val });
                                    }}
                                    helperText={
                                        editingItem.durationMode === 'dynamic'
                                            ? `A $${editingItem.startDollarAmount} tip will run for approx ${
                                                  (editingItem.startDollarAmount || 0) * (editingItem.durationPerDollar || 0)
                                              } seconds.`
                                            : ''
                                    }
                                    InputProps={{
                                        startAdornment: <TimerIcon color="action" sx={{ mr: 1 }} />,
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                sec{editingItem.durationMode === 'dynamic' ? ' / $' : ''}
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        </Grid>

                        {/* Intensity Slider (0-100%) */}
                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Intensity
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Adjust the vibration intensity for this tip range.
                        </Typography>
                        <Box
                            sx={{
                                bgcolor: 'action.hover',
                                px: 3,
                                py: 1,
                                borderRadius: theme => theme.shape.borderRadius,
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <span>Vibrate Level</span>
                                <span>{toPercent(editingItem.intensity)}%</span>
                            </Typography>
                            <Slider
                                value={toPercent(editingItem.intensity)}
                                min={0}
                                max={100}
                                step={5}
                                marks={[
                                    { value: 0, label: '0%' },
                                    { value: 50, label: '50%' },
                                    { value: 100, label: '100%' },
                                ]}
                                valueLabelDisplay="auto"
                                valueLabelFormat={v => `${v}%`}
                                onChange={(_, value) => {
                                    setEditingItem({ ...editingItem, intensity: toIntensity(value as number) });
                                }}
                            />
                        </Box>
                    </DialogContent>
                )}

                <StyledDialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" disabled={!editingItem || !!validationError || isUpdatingSettings}>
                        {editingItem?.settingId ? 'Update' : 'Save'}
                    </Button>
                </StyledDialogActions>
            </DialogAdaptive>
        </Box>
    );
};

export default ToySettingsEditor;
